/* eslint-disable no-unused-vars */
/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CONNEXION_PRETE } from './ConnexionCompteur';

// ── Brand ──
const V = {
  volt: '#C8F135', deep: '#06080C', card: '#0D1117', card2: '#161616',
  border: 'rgba(255,255,255,0.07)', muted: 'rgba(238,242,255,0.45)',
  text: '#EEF2FF', red: '#ff4444', orange: '#f59e0b', green: '#4ade80', blue: '#4fc3f7',
};
const title = { fontFamily: "'Lexend', sans-serif" };
const body  = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const mono  = { fontFamily: "'IBM Plex Mono', monospace" };

// ── Simulated Data ──
const DATA_30 = [
  0.07,0.06,0.05,0.05,0.05,0.06, 0.07,0.08,0.06,0.05,0.05,0.06,
  0.14,0.22,0.26,0.24,0.20,0.18, 0.19,0.24,0.28,0.27,0.25,0.22,
  0.17,0.18,0.20,0.21,0.24,0.26, 0.28,0.26,0.27,0.29,0.31,0.33,
  0.55,0.60,0.70,0.72,0.70,0.68, 0.55,0.44,0.30,0.24,0.18,0.12,
];
const PEAK_IDX = DATA_30.indexOf(Math.max(...DATA_30));
const AVG_30   = +(DATA_30.reduce((a, b) => a + b, 0) / DATA_30.length).toFixed(3);

const INIT_APPLIANCES = [
  { id: 'tv',  level: 'forte',   icon: '📺', name: 'Télévision',    watt: 150,  bar: 85, color: V.red,    status: 'actif' },
  { id: 'fer', level: 'forte',   icon: '🔥', name: 'Fer à repasser',watt: 2400, bar: 95, color: V.red,    status: null },
  { id: 'ps',  level: 'moyenne', icon: '🎮', name: 'PlayStation',   watt: 120,  bar: 55, color: V.orange, status: 'actif' },
  { id: 'tel', level: 'faible',  icon: '📱', name: 'Chargeur tél.', watt: 5,    bar: 10, color: V.green,  status: null },
];

const DEVICE_OPTIONS = [
  { icon: '🧺', name: 'Lave-linge',     watt: 2000, level: 'forte',   bar: 88, color: V.red },
  { icon: '🍽️', name: 'Lave-vaisselle', watt: 1800, level: 'forte',   bar: 80, color: V.red },
  { icon: '❄️', name: 'Réfrigérateur',  watt: 150,  level: 'moyenne', bar: 45, color: V.orange },
  { icon: '☕', name: 'Cafetière',       watt: 900,  level: 'moyenne', bar: 50, color: V.orange },
  { icon: '💡', name: 'Éclairage',       watt: 40,   level: 'faible',  bar: 15, color: V.green },
  { icon: '🔌', name: 'Radiateur',       watt: 1500, level: 'forte',   bar: 75, color: V.red },
  { icon: '🖥️', name: 'PC Bureau',       watt: 200,  level: 'moyenne', bar: 52, color: V.orange },
  { icon: '🚗', name: 'Borne VE',        watt: 7400, level: 'forte',   bar: 99, color: V.red },
  { icon: '🔋', name: 'Autre',           watt: 100,  level: 'moyenne', bar: 40, color: V.orange },
];

// ── Reusable ──
const Card = ({ children, style }) => (
  <div style={{ background: V.card, border: `1px solid ${V.border}`, borderRadius: 14, padding: 18, ...style }}>
    {children}
  </div>
);

const CardTitle = ({ children, style }) => (
  <div style={{ ...body, fontSize: '.68rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: V.muted, ...style }}>
    {children}
  </div>
);

const Badge = ({ children, type = 'new' }) => {
  const bg = type === 'new' ? 'rgba(200,241,53,0.12)' : type === 'alert' ? 'rgba(255,68,68,0.12)' : 'rgba(79,195,247,0.12)';
  const c  = type === 'new' ? V.volt : type === 'alert' ? V.red : V.blue;
  return (
    <span style={{ fontSize: '.65rem', padding: '3px 9px', borderRadius: 20, fontWeight: 700, background: bg, color: c, ...body }}>
      {children}
    </span>
  );
};

// ── Bar Chart ──
const BarChart = ({ compact = false }) => {
  const W = 800, H = compact ? 280 : 380, P = { t: 24, r: 12, b: 36, l: 38 };
  const cW = W - P.l - P.r, cH = H - P.t - P.b;
  const N = DATA_30.length, maxV = 0.8;
  const barW = Math.max((cW / N) * 0.7, 2);
  const barCol = v => v > 0.5 ? V.red : v > 0.25 ? V.orange : V.green;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {[0, 0.2, 0.4, 0.6, 0.8].map(v => {
        const y = P.t + cH - (v / maxV) * cH;
        return (
          <g key={v}>
            <line x1={P.l} y1={y} x2={P.l + cW} y2={y} stroke="rgba(255,255,255,0.05)" />
            <text x={P.l - 4} y={y + 4} fill="rgba(255,255,255,0.3)" fontSize="9" textAnchor="end">{v.toFixed(1)}</text>
          </g>
        );
      })}
      {DATA_30.map((v, i) => {
        const cv = Math.min(v, maxV);
        const x  = P.l + (i / N) * cW + (cW / N - barW) / 2;
        const bH = (cv / maxV) * cH;
        const y  = P.t + cH - bH;
        const color = barCol(v);
        return (
          <rect key={i} x={x} y={y} width={barW} height={bH} rx={2}
            fill={color} opacity={i === PEAK_IDX ? 1 : 0.75} />
        );
      })}
      <line
        x1={P.l} y1={P.t + cH - (Math.min(AVG_30, maxV) / maxV) * cH}
        x2={P.l + cW} y2={P.t + cH - (Math.min(AVG_30, maxV) / maxV) * cH}
        stroke="rgba(200,241,53,0.5)" strokeWidth="1.5" strokeDasharray="4 4" />
      {Array.from({ length: 5 }, (_, i) => i * 12).map(i => (
        <text key={i} x={P.l + (i / N) * cW + (cW / N) / 2} y={P.t + cH + 16}
          fill="rgba(255,255,255,0.3)" fontSize="8" textAnchor="middle">{(i / 2)}h</text>
      ))}
    </svg>
  );
};

// ── Suggestions ──
const SUGGESTIONS = [
  { id: 0, icon: '🧺', title: 'Décaler le lave-linge en heures creuses', saving: '↓ ~38€/an', detail: 'Passer le lave-linge de 20h30 (HP) à 23h (HC) économise ~0.15€/lavage. Sur 3 lavages/semaine → 38€/an.' },
  { id: 1, icon: '🌡️', title: 'Veille TV + box — consommation fantôme', saving: '↓ ~28€/an', detail: '80W entre 0h et 7h = 204 kWh/an × 0.2516€ = ~51€. Multiprise à minuterie → économie de 28€/an.' },
  { id: 2, icon: '⚡', title: 'Passer en HP/HC — compatible avec votre profil', saving: '↓ ~92€/an', detail: '42% de votre conso déjà en HC. Simulation 90j : Base 141€ vs HP/HC 118€ = -23€/trimestre = 92€/an.' },
];

function SuggestionsTab() {
  const [expanded, setExpanded] = useState({});
  const [ignored,  setIgnored]  = useState({});
  const visible = SUGGESTIONS.filter(s => !ignored[s.id]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {visible.length === 0 && (
        <p style={{ textAlign: 'center', color: V.muted, fontSize: '.82rem', padding: '20px 0', ...body }}>
          Toutes les suggestions ont été traitées.
        </p>
      )}
      {visible.map(s => (
        <div key={s.id} style={{
          padding: '14px 16px', background: V.card2, borderRadius: 12,
          border: `1px solid ${expanded[s.id] ? 'rgba(200,241,53,0.2)' : V.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: '1.2rem', flexShrink: 0, marginTop: 1 }}>{s.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '.82rem', fontWeight: 600, color: V.text, marginBottom: 4, ...title }}>{s.title}</div>
              <div style={{ ...mono, fontSize: '.75rem', fontWeight: 700, color: V.volt }}>{s.saving}</div>
            </div>
          </div>
          {expanded[s.id] && (
            <div style={{ margin: '8px 0', padding: '10px 12px', background: 'rgba(200,241,53,0.04)', border: '1px solid rgba(200,241,53,0.12)', borderRadius: 10, fontSize: '.78rem', color: 'rgba(238,242,255,0.7)', lineHeight: 1.6, ...body }}>
              {s.detail}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={() => setExpanded(p => ({ ...p, [s.id]: !p[s.id] }))} style={{
              background: V.volt, border: 'none', color: '#000', padding: '7px 14px',
              borderRadius: 8, cursor: 'pointer', ...title, fontWeight: 700, fontSize: '.75rem',
            }}>
              {expanded[s.id] ? 'Masquer' : 'Voir le détail'}
            </button>
            <button onClick={() => setIgnored(p => ({ ...p, [s.id]: true }))} style={{
              background: 'none', border: `1px solid ${V.border}`, color: V.muted,
              padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontSize: '.75rem', ...body,
            }}>Ignorer</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Schéma compteur (desktop only) ──
const CompteurSchema = ({ appliances, onRemove }) => {
  const canvasRef   = useRef(null);
  const hitAreasRef = useRef([]);
  const [tooltip, setTooltip] = useState(null);

  const draw = useCallback(() => {
    const sc = canvasRef.current;
    if (!sc) return;
    const W = sc.offsetWidth;
    const H = Math.max(300, W * 0.55);
    sc.width = W; sc.height = H; sc.style.height = H + 'px';
    const ctx = sc.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    hitAreasRef.current = [];
    const cx = W / 2, cy = H / 2, rad = Math.min(W, H) * 0.31;

    const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad * 1.3);
    bg.addColorStop(0, 'rgba(200,241,53,0.05)'); bg.addColorStop(1, 'transparent');
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

    ctx.beginPath(); ctx.arc(cx, cy, rad, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.05)'; ctx.lineWidth = 1;
    ctx.setLineDash([4, 6]); ctx.stroke(); ctx.setLineDash([]);

    const rr = (c, x, y, w, h, r) => {
      c.beginPath(); c.moveTo(x + r, y); c.lineTo(x + w - r, y); c.quadraticCurveTo(x + w, y, x + w, y + r);
      c.lineTo(x + w, y + h - r); c.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      c.lineTo(x + r, y + h); c.quadraticCurveTo(x, y + h, x, y + h - r);
      c.lineTo(x, y + r); c.quadraticCurveTo(x, y, x + r, y); c.closePath();
    };

    appliances.forEach((dev, i) => {
      const angle = (i / appliances.length) * Math.PI * 2 - Math.PI / 2;
      const dx = cx + Math.cos(angle) * rad, dy = cy + Math.sin(angle) * rad;
      const lg = ctx.createLinearGradient(cx, cy, dx, dy);
      lg.addColorStop(0, 'rgba(200,241,53,0.5)'); lg.addColorStop(1, dev.color + '66');
      ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(dx, dy);
      ctx.strokeStyle = lg; ctx.lineWidth = 1.5; ctx.stroke();
      const bw = 86, bh = 60, bx = dx - bw / 2, by = dy - bh / 2;
      ctx.fillStyle = '#161616'; rr(ctx, bx, by, bw, bh, 10); ctx.fill();
      ctx.strokeStyle = dev.color + '55'; ctx.lineWidth = 1.5; rr(ctx, bx, by, bw, bh, 10); ctx.stroke();
      ctx.font = '18px serif'; ctx.textAlign = 'center'; ctx.fillText(dev.icon, dx, dy - 6);
      ctx.font = "600 9px 'Plus Jakarta Sans', sans-serif"; ctx.fillStyle = '#e8e8e8';
      ctx.fillText(dev.name.length > 12 ? dev.name.slice(0, 11) + '…' : dev.name, dx, dy + 8);
      ctx.font = "8.5px 'IBM Plex Mono', monospace"; ctx.fillStyle = dev.color;
      ctx.fillText(dev.watt + 'W', dx, dy + 20);
      hitAreasRef.current.push({ type: 'device', id: dev.id, x: bx, y: by, w: bw, h: bh, dev });
    });

    const hl = ctx.createRadialGradient(cx, cy, 26, cx, cy, 62);
    hl.addColorStop(0, 'rgba(200,241,53,0.14)'); hl.addColorStop(1, 'transparent');
    ctx.fillStyle = hl; ctx.beginPath(); ctx.arc(cx, cy, 62, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(cx, cy, 38, 0, Math.PI * 2);
    ctx.fillStyle = '#0D1117'; ctx.fill();
    ctx.strokeStyle = 'rgba(200,241,53,0.7)'; ctx.lineWidth = 2; ctx.stroke();
    ctx.font = '22px serif'; ctx.textAlign = 'center'; ctx.fillText('⚡', cx, cy + 4);
    ctx.font = "bold 8px 'Lexend', sans-serif"; ctx.fillStyle = V.volt; ctx.fillText('COMPTEUR', cx, cy + 19);
    ctx.font = "7px 'Plus Jakarta Sans', sans-serif"; ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillText(appliances.length + ' app.', cx, cy + 30);
  }, [appliances]);

  const handleClick = (e) => {
    const sc = canvasRef.current; if (!sc) return;
    const rect = sc.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (sc.width / rect.width);
    const my = (e.clientY - rect.top) * (sc.height / rect.height);
    const wrap = sc.parentElement, wrapRect = wrap.getBoundingClientRect();
    const relX = e.clientX - wrapRect.left, relY = e.clientY - wrapRect.top;
    for (const area of hitAreasRef.current) {
      if (mx >= area.x && mx <= area.x + area.w && my >= area.y && my <= area.y + area.h) {
        setTooltip({ type: 'device', x: Math.min(relX + 10, wrap.offsetWidth - 200), y: Math.max(relY - 120, 6), dev: area.dev });
        return;
      }
    }
    setTooltip(null);
  };

  useEffect(() => { draw(); }, [draw]);
  useEffect(() => {
    const h = () => { draw(); setTooltip(null); };
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [draw]);

  return (
    <Card>
      <CardTitle style={{ marginBottom: 12 }}>Schéma de consommation · cliquez sur un appareil pour le gérer</CardTitle>
      <div style={{ position: 'relative' }}>
        <canvas ref={canvasRef} onClick={handleClick} style={{ width: '100%', display: 'block', cursor: 'pointer' }} />
        {tooltip?.type === 'device' && tooltip.dev && (
          <div style={{ position: 'absolute', left: tooltip.x, top: tooltip.y, width: 180, background: '#1c1c1c', border: '1px solid rgba(200,241,53,0.3)', borderRadius: 12, padding: '14px 16px', zIndex: 50 }}>
            <div style={{ fontWeight: 600, fontSize: '.83rem', marginBottom: 3, ...title }}>{tooltip.dev.name}</div>
            <div style={{ ...mono, fontSize: '.73rem', color: V.muted, marginBottom: 10 }}>~{tooltip.dev.watt}W</div>
            <div style={{ display: 'flex', gap: 7 }}>
              <button onClick={() => { onRemove(tooltip.dev.id); setTooltip(null); }} style={{ background: 'none', border: '1px solid rgba(255,68,68,0.3)', color: V.red, padding: '6px 10px', borderRadius: 7, cursor: 'pointer', fontSize: '.73rem', ...body }}>Supprimer</button>
              <button onClick={() => setTooltip(null)} style={{ background: 'none', border: `1px solid ${V.border}`, color: V.muted, padding: '6px 10px', borderRadius: 7, cursor: 'pointer', fontSize: '.73rem', ...body }}>Fermer</button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

// ════════════════════════════════════════
//  MAIN
// ════════════════════════════════════════
export default function MonCompteurPage({ reco, onNavigate, onConnectMeter }) {
  const [activeTab,     setActiveTab]     = useState('signatures');
  const [appliances,    setAppliances]    = useState(INIT_APPLIANCES);
  const [showModal,     setShowModal]     = useState(false);
  const [selectedDev,   setSelectedDev]   = useState(null);
  const [alertVisible,  setAlertVisible]  = useState(true);
  const [period,        setPeriod]        = useState("Aujourd'hui");
  const [isDesktop,     setIsDesktop]     = useState(window.innerWidth >= 900);

  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const removeAppliance = (id) => setAppliances(p => p.filter(a => a.id !== id));
  const addAppliance = () => {
    if (!selectedDev) return;
    setAppliances(p => [...p, { ...selectedDev, id: selectedDev.name.toLowerCase().replace(/\s/g, '-') + '-' + Date.now(), status: null }]);
    setShowModal(false); setSelectedDev(null);
  };

  const tabs = [
    { id: 'signatures', label: isDesktop ? '🔍 Détection appareils' : '🔍 Détection' },
    { id: 'hphc',       label: isDesktop ? '⏱ Analyse HP/HC'        : '⏱ HP/HC' },
    { id: 'power',      label: isDesktop ? '⚡ Puissance souscrite'  : '⚡ Puissance' },
    { id: 'suggestions',label: isDesktop ? '💡 Suggestions IA'       : '💡 Conseils' },
  ];

  // ── Styles helpers ──
  const rowStyle  = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${V.border}`, fontSize: '.8rem' };
  const labelStyle = { color: V.muted, ...body };
  const valStyle   = { ...mono, color: V.text };

  return (
    <div style={{ display: 'flex', gap: isDesktop ? 18 : 0, flexDirection: isDesktop ? 'row' : 'column' }}>

      {/* ══ MAIN COLUMN ══ */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* BANDEAU DÉMONSTRATION — affiché tant que le compteur n'est pas connecté */}
        <div role="note" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.45)', borderRadius: 12, padding: '12px 14px' }}>
          <span style={{ background: V.orange, color: '#000', borderRadius: 6, padding: '2px 8px', fontSize: '.66rem', fontWeight: 800, letterSpacing: '.06em', flexShrink: 0, ...title }}>DÉMO</span>
          <div style={{ fontSize: '.78rem', lineHeight: 1.5, color: V.text, ...body }}>
            <strong>Données de démonstration.</strong> Les chiffres, courbes et appareils de cette page sont des exemples : ce ne sont pas vos consommations réelles. Elles s'afficheront ici une fois votre compteur connecté.
            {CONNEXION_PRETE && onConnectMeter && (
              <div style={{ marginTop: 10 }}>
                <button onClick={onConnectMeter} style={{ background: V.volt, color: V.deep, border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: '.78rem', cursor: 'pointer', ...title }}>
                  Connecter mon compteur Linky
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ALERT */}
        {alertVisible && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,68,68,0.07)', border: '1px solid rgba(255,68,68,0.25)', borderRadius: 12, padding: '12px 14px', cursor: 'pointer' }}
            onClick={() => setAlertVisible(false)}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: V.red, flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: '.8rem', lineHeight: 1.5, ...body }}>
              <strong style={{ color: '#fff' }}>Pic détecté 19h–20h</strong> — +75% vs moyenne. 2 appareils identifiés.
            </div>
            <span style={{ background: V.red, color: '#fff', borderRadius: 20, padding: '3px 10px', fontSize: '.7rem', fontWeight: 700, flexShrink: 0 }}>Voir →</span>
          </div>
        )}

        {/* HEADER */}
        <div>
          <h1 style={{ ...title, fontSize: isDesktop ? '1.9rem' : '1.5rem', fontWeight: 800, margin: '0 0 4px' }}>
            Mon <span style={{ color: V.volt }}>compteur</span>
          </h1>
          <p style={{ color: V.muted, fontSize: '.78rem', margin: 0, ...body }}>
            Aperçu de démonstration · données d'exemple
          </p>
        </div>

        {/* STATS — 2 colonnes sur mobile, 4 sur desktop */}
        <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)', gap: 10 }}>
          {[
            { label: "Aujourd'hui", val: '8.4', unit: 'kWh', sub: '↑ +1.2 vs hier', upColor: V.red },
            { label: 'Ce mois',     val: '187',  unit: 'kWh', sub: '↓ -8% vs M-1',  upColor: V.green },
            { label: 'Coût estimé', val: '47€',  sub: 'sur 340 kWh estimés' },
            { label: 'Économies',   val: '112€', sub: "depuis l'abonnement", valColor: V.volt },
          ].map((s, i) => (
            <div key={i} style={{ background: V.card2, border: `1px solid ${V.border}`, borderRadius: 12, padding: '14px 14px' }}>
              <div style={{ fontSize: '.65rem', color: V.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.07em', ...body }}>{s.label}</div>
              <div style={{ ...mono, fontSize: '1.25rem', fontWeight: 700, color: s.valColor || '#fff' }}>
                {s.val}{s.unit && <span style={{ fontSize: '.8rem', color: V.muted }}> {s.unit}</span>}
              </div>
              <div style={{ fontSize: '.67rem', color: s.upColor || V.muted, marginTop: 3, ...body }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* CHART */}
        <Card style={{ padding: isDesktop ? 20 : 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8, flexWrap: 'wrap' }}>
            <CardTitle>Courbe de charge · {period === "Aujourd'hui" ? '30 min' : period}</CardTitle>
            <div style={{ display: 'flex', gap: 3, background: V.card2, borderRadius: 8, padding: 3 }}>
              {["Aujourd'hui", 'Semaine', 'Mois'].map(p => (
                <button key={p} onClick={() => setPeriod(p)} style={{
                  background: period === p ? V.card : 'none',
                  border: period === p ? `1px solid rgba(200,241,53,0.2)` : 'none',
                  color: period === p ? V.volt : V.muted,
                  ...body, fontSize: '.72rem', padding: isDesktop ? '5px 12px' : '4px 8px',
                  borderRadius: 6, cursor: 'pointer',
                }}>{isDesktop ? p : p === "Aujourd'hui" ? 'Auj.' : p}</button>
              ))}
            </div>
          </div>
          <BarChart compact={!isDesktop} />
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
            {[{ c: V.green, l: 'Faible' }, { c: V.orange, l: 'Moyen' }, { c: V.red, l: 'Pic' }].map(x => (
              <div key={x.l} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.72rem', color: V.muted, ...body }}>
                <div style={{ width: 9, height: 9, borderRadius: 2, background: x.c, flexShrink: 0 }} />{x.l}
              </div>
            ))}
          </div>
        </Card>

        {/* TABS */}
        <Card style={{ padding: isDesktop ? 20 : 14 }}>
          {/* Tabs header — scroll horizontal sur mobile */}
          <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${V.border}`, marginBottom: 18, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                background: 'none', border: 'none',
                color: activeTab === t.id ? V.volt : V.muted,
                ...body, fontSize: '.78rem', padding: isDesktop ? '10px 16px' : '10px 12px',
                cursor: 'pointer', borderBottom: `2px solid ${activeTab === t.id ? V.volt : 'transparent'}`,
                marginBottom: -1, whiteSpace: 'nowrap', flexShrink: 0,
              }}>{t.label}</button>
            ))}
          </div>

          {/* TAB: SIGNATURES */}
          {activeTab === 'signatures' && (
            <div>
              <p style={{ fontSize: '.78rem', color: V.muted, marginBottom: 14, lineHeight: 1.55, ...body }}>
                Nowatt analyse votre courbe 30 min et identifie les <strong style={{ color: '#fff' }}>signatures de consommation</strong> de chaque appareil.
              </p>
              {/* Liste simple sur mobile, grille sur desktop */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { icon: '🧺', name: 'Lave-linge',    detail: '3×/sem · 20h–22h',  kwh: '2.1', unit: '/cycle', conf: 94, color: V.red },
                  { icon: '📺', name: 'Télévision',     detail: 'Chaque soir 19h–23h', kwh: '0.6', unit: '/soir',  conf: 91, color: V.orange },
                  { icon: '❄️', name: 'Réfrigérateur',  detail: '24h/24',              kwh: '1.2', unit: '/jour',  conf: 99, color: V.green },
                  { icon: '🍽️', name: 'Lave-vaisselle', detail: '1×/jour · 23h–1h',   kwh: '1.5', unit: '/cycle', conf: 87, color: V.orange },
                ].map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: V.card2, border: `1px solid ${V.border}`, borderRadius: 12 }}>
                    <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>{s.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '.82rem', fontWeight: 600, ...title, marginBottom: 2 }}>{s.name}</div>
                      <div style={{ fontSize: '.72rem', color: V.muted, ...body }}>{s.detail}</div>
                      <div style={{ height: 3, borderRadius: 3, marginTop: 6, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 3, width: `${s.conf}%`, background: s.color }} />
                      </div>
                      <div style={{ fontSize: '.62rem', color: V.muted, marginTop: 2, ...body }}>Confiance {s.conf}%</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ ...mono, fontSize: '.85rem', fontWeight: 700, color: s.color }}>{s.kwh} kWh</div>
                      <div style={{ fontSize: '.65rem', color: V.muted, ...body }}>{s.unit}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '.72rem', color: V.muted, marginTop: 12, ...body }}>
                + 3 appareils non identifiés · <span style={{ color: V.volt, cursor: 'pointer' }}>Aider Nowatt →</span>
              </p>
            </div>
          )}

          {/* TAB: HP/HC */}
          {activeTab === 'hphc' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 10 }}>
                <div>
                  <div style={{ fontSize: '.82rem', fontWeight: 600, color: '#fff', marginBottom: 3, ...title }}>Votre profil est-il compatible HP/HC ?</div>
                  <div style={{ fontSize: '.73rem', color: V.muted, ...body }}>Exemple calculé sur 90 jours</div>
                </div>
                <Badge type="new">✅ Compatible</Badge>
              </div>
              <div style={{ display: 'flex', height: 28, borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
                <div style={{ width: '58%', background: 'rgba(255,68,68,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.68rem', fontWeight: 700, color: '#fff', ...body }}>HP · 58%</div>
                <div style={{ width: '42%', background: 'rgba(74,222,128,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.68rem', fontWeight: 700, color: '#fff', ...body }}>HC · 42%</div>
              </div>
              {[
                ['Tarif actuel (Base)',       '0.2516€/kWh',  null],
                ['Coût Base (90j)',           '141€',         null],
                ['Coût HP/HC simulé (90j)',   '118€ (−23€)',  V.green],
                ['Gain annuel estimé',        '~92€/an',      V.volt],
              ].map(([l, v, c], i) => (
                <div key={i} style={rowStyle}>
                  <span style={labelStyle}>{l}</span>
                  <span style={{ ...mono, color: c || V.text, fontWeight: c ? 700 : 400 }}>{v}</span>
                </div>
              ))}
              <div style={{ marginTop: 14, padding: '12px 14px', background: 'rgba(200,241,53,0.05)', border: '1px solid rgba(200,241,53,0.15)', borderRadius: 10, fontSize: '.76rem', color: V.muted, lineHeight: 1.55, ...body }}>
                💡 Décaler lave-linge et lave-vaisselle en HC (22h–6h). Nowatt peut vous le rappeler.
              </div>
              <button style={{ marginTop: 14, background: V.volt, border: 'none', color: '#000', padding: '10px 18px', borderRadius: 9, cursor: 'pointer', ...title, fontWeight: 700, fontSize: '.8rem' }}>
                Simuler le switch HP/HC
              </button>
            </div>
          )}

          {/* TAB: PUISSANCE */}
          {activeTab === 'power' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 10 }}>
                <div>
                  <div style={{ fontSize: '.82rem', fontWeight: 600, color: '#fff', marginBottom: 3, ...title }}>
                    Puissance souscrite : <span style={{ color: V.volt, ...mono }}>9 kVA</span>
                  </div>
                  <div style={{ fontSize: '.73rem', color: V.muted, ...body }}>Analyse sur 90 jours</div>
                </div>
                <Badge type="info">💰 Surdimensionné</Badge>
              </div>
              <div style={{ position: 'relative', height: 10, background: 'rgba(255,255,255,0.06)', borderRadius: 5, overflow: 'hidden', margin: '14px 0 6px' }}>
                <div style={{ height: '100%', width: '42%', borderRadius: 5, background: `linear-gradient(90deg,${V.green},${V.orange})` }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.68rem', color: V.muted, marginBottom: 16, ...body }}>
                <span>0 kW</span><span style={{ color: V.orange }}>Votre pic : 3.8 kW</span><span>9 kVA</span>
              </div>
              {[
                ['Puissance actuelle',      '9 kVA · 147€/an',  null],
                ['Pic max mesuré (90j)',    '3.8 kW',           V.orange],
                ['Recommandée',            '6 kVA · 112€/an',  V.volt],
                ['Économie abonnement',    '~35€/an',          V.volt],
              ].map(([l, v, c], i) => (
                <div key={i} style={rowStyle}>
                  <span style={labelStyle}>{l}</span>
                  <span style={{ ...mono, color: c || V.text, fontWeight: c ? 700 : 400 }}>{v}</span>
                </div>
              ))}
              <div style={{ marginTop: 14, padding: '12px 14px', background: 'rgba(200,241,53,0.04)', border: '1px solid rgba(200,241,53,0.15)', borderRadius: 10, fontSize: '.76rem', color: V.muted, lineHeight: 1.55, ...body }}>
                🔌 Nowatt surveille votre pic en continu. Après 3 mois sous 6 kVA, il vous suggérera de baisser votre abonnement.
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ height: 5, flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: '33%', background: V.volt, borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: '.7rem', color: V.muted, whiteSpace: 'nowrap', ...mono }}>1 / 3 mois</span>
                </div>
              </div>
            </div>
          )}

          {/* TAB: SUGGESTIONS */}
          {activeTab === 'suggestions' && <SuggestionsTab />}
        </Card>

        {/* APPAREILS DÉCLARÉS */}
        <Card style={{ padding: isDesktop ? 20 : 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <CardTitle>Mes appareils déclarés</CardTitle>
            <button onClick={() => setShowModal(true)} style={{
              background: V.volt, border: 'none', color: '#000', padding: '7px 14px',
              borderRadius: 9, cursor: 'pointer', ...title, fontWeight: 700, fontSize: '.75rem',
            }}>+ Ajouter</button>
          </div>
          {['forte', 'moyenne', 'faible'].map(level => {
            const items = appliances.filter(a => a.level === level);
            if (items.length === 0) return null;
            const lc = level === 'forte' ? V.red : level === 'moyenne' ? V.orange : V.green;
            const lt = level === 'forte' ? 'Forte conso.' : level === 'moyenne' ? 'Conso. moyenne' : 'Faible conso.';
            return (
              <div key={level}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.66rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', margin: '6px 0 6px', color: lc, ...body }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: lc }} />
                  {lt}
                  <span style={{ marginLeft: 'auto', background: V.card2, borderRadius: 20, padding: '2px 8px', color: V.muted, ...mono }}>{items.length}</span>
                </div>
                {items.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: V.card2, border: `1px solid ${V.border}`, borderRadius: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: '1rem', flexShrink: 0 }}>{a.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '.8rem', fontWeight: 500, ...title }}>{a.name}</div>
                      <div style={{ fontSize: '.67rem', color: V.muted, ...mono }}>~{a.watt}W{a.status ? ` · ${a.status}` : ''}</div>
                    </div>
                    <div style={{ width: 50, height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
                      <div style={{ height: '100%', width: `${a.bar}%`, background: a.color, borderRadius: 3 }} />
                    </div>
                    <button onClick={() => removeAppliance(a.id)} style={{ background: 'none', border: 'none', color: V.muted, fontSize: '1rem', cursor: 'pointer', padding: '2px 4px', flexShrink: 0 }}>✕</button>
                  </div>
                ))}
              </div>
            );
          })}
        </Card>

        {/* SCHEMA — desktop seulement */}
        {isDesktop && <CompteurSchema appliances={appliances} onRemove={removeAppliance} />}
      </div>

      {/* ══ RIGHT SIDEBAR — desktop seulement ══ */}
      {isDesktop && (
        <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Agent */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'rgba(200,241,53,0.04)', border: '1px solid rgba(200,241,53,0.12)', borderRadius: 12 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: V.volt, boxShadow: `0 0 8px ${V.volt}`, flexShrink: 0 }} />
            <div style={{ fontSize: '.77rem', lineHeight: 1.5, ...body }}>
              Agent actif · <strong style={{ color: V.volt }}>surveillance en cours</strong><br />
              Dernière analyse il y a 3 min
            </div>
          </div>

          {/* Fournisseur actuel */}
          <div style={{ background: V.card, border: '1px solid rgba(200,241,53,0.15)', borderRadius: 14, padding: 18 }}>
            <div style={{ fontSize: '.66rem', color: V.muted, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 8, ...body }}>Fournisseur actuel</div>
            <div style={{ ...title, fontSize: '1rem', fontWeight: 700, color: '#fff', marginBottom: 2 }}>EDF Tarif Bleu</div>
            <div style={{ fontSize: '.75rem', color: V.muted, marginBottom: 12, ...body }}>Offre standard · HP/HC · 9 kVA</div>
            <div style={{ ...mono, fontSize: '1.7rem', fontWeight: 700, color: V.volt }}>0.2516€ <span style={{ fontSize: '.8rem', color: V.muted }}>/ kWh</span></div>
            <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(200,241,53,0.08)', borderRadius: 8, fontSize: '.74rem', color: V.volt, fontWeight: 600, ...body }}>
              ⚡ 112€ économisés depuis le début
            </div>
          </div>

          {/* Meilleure offre */}
          <Card style={{ borderColor: 'rgba(200,241,53,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <CardTitle>Meilleure offre</CardTitle>
              <Badge type="new">Nouveau</Badge>
            </div>
            <div style={{ ...title, fontSize: '1rem', fontWeight: 700, color: '#fff', marginBottom: 2 }}>Octopus Energy Flex</div>
            <div style={{ fontSize: '.73rem', color: V.muted, marginBottom: 12, ...body }}>HP/HC adaptatif · indexé marché</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: '.66rem', color: V.muted, ...body }}>Coût/an</div>
                <div style={{ ...mono, fontSize: '1.15rem', fontWeight: 700, color: V.volt }}>1 620€</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '.66rem', color: V.muted, ...body }}>vs maintenant</div>
                <div style={{ ...mono, fontSize: '1.15rem', fontWeight: 700, color: V.green }}>-220€</div>
              </div>
            </div>
            <button onClick={() => onNavigate && onNavigate('comparateur')} style={{ background: V.volt, border: 'none', color: '#000', padding: '8px 14px', borderRadius: 9, cursor: 'pointer', ...title, fontWeight: 700, fontSize: '.78rem', width: '100%' }}>
              Voir les détails du switch
            </button>
          </Card>

          {/* Comparaison mensuelle */}
          <Card>
            <CardTitle style={{ marginBottom: 12 }}>Comparaison mensuelle</CardTitle>
            {[['Janvier','312'],['Février','298'],['Mars','265'],['Avril','187',true]].map(([m, v, cur], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < 3 ? `1px solid ${V.border}` : 'none', fontSize: '.78rem' }}>
                <span style={{ color: V.muted, ...body }}>{m}</span>
                <span style={{ ...mono, color: cur ? V.volt : V.text }}>{v} kWh{cur ? ' ↓' : ''}</span>
              </div>
            ))}
          </Card>

          {/* Score énergie */}
          <Card>
            <CardTitle style={{ marginBottom: 12 }}>Score énergie Nowatt</CardTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ ...mono, fontSize: '2.5rem', fontWeight: 700, color: V.volt, lineHeight: 1 }}>B+</div>
              <div>
                <div style={{ fontSize: '.78rem', color: '#fff', marginBottom: 3, ...body }}>Meilleur que 68% des foyers</div>
                <div style={{ fontSize: '.7rem', color: V.muted, ...body }}>4 pers. · maison · Île-de-France</div>
              </div>
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 3 }}>
              {[V.red, V.orange, '#facc15', V.volt, 'rgba(255,255,255,0.08)'].map((c, i) => (
                <div key={i} style={{ flex: 1, height: 5, borderRadius: 3, background: c }} />
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.62rem', color: V.muted, marginTop: 4, ...body }}>
              <span>F</span><span>D</span><span>C</span><span style={{ color: V.volt }}>B+</span><span>A</span>
            </div>
          </Card>
        </div>
      )}

      {/* MODAL AJOUT APPAREIL */}
      {showModal && (
        <div onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)',
          zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div style={{ background: V.card, border: `1px solid ${V.border}`, borderRadius: 18, padding: 24, width: '100%', maxWidth: 380 }}>
            <h3 style={{ ...title, fontSize: '1rem', fontWeight: 700, marginBottom: 4 }}>Ajouter un appareil</h3>
            <p style={{ fontSize: '.78rem', color: V.muted, marginBottom: 18, ...body }}>Sélectionnez un appareil pour enrichir la détection.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 18 }}>
              {DEVICE_OPTIONS.map((d, i) => (
                <div key={i} onClick={() => setSelectedDev(d)} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                  padding: '12px 6px', background: selectedDev === d ? 'rgba(200,241,53,0.05)' : V.card2,
                  border: `1px solid ${selectedDev === d ? 'rgba(200,241,53,0.4)' : V.border}`,
                  borderRadius: 10, cursor: 'pointer', fontSize: '.68rem',
                  color: selectedDev === d ? V.volt : V.muted, textAlign: 'center', ...body,
                }}>
                  <span style={{ fontSize: '1.3rem' }}>{d.icon}</span>{d.name}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 9, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowModal(false); setSelectedDev(null); }} style={{ background: 'none', border: `1px solid ${V.border}`, color: V.muted, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '.78rem', ...body }}>Annuler</button>
              <button onClick={addAppliance} style={{ background: V.volt, border: 'none', color: '#000', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', ...title, fontWeight: 700, fontSize: '.78rem' }}>Ajouter</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
